import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { strategySymbolApi } from '../../api/strategySymbols';

// Async thunks
export const fetchUserAssociations = createAsyncThunk(
  'strategySymbols/fetchUserAssociations',
  async (activeOnly = false, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.getUserAssociations(activeOnly);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchSymbolsForStrategy = createAsyncThunk(
  'strategySymbols/fetchSymbolsForStrategy',
  async (strategyId, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.getSymbolsForStrategy(strategyId);
      return { strategyId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const activateStrategy = createAsyncThunk(
  'strategySymbols/activateStrategy',
  async ({ strategyId, symbol, config }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.activateStrategy(strategyId, symbol, config);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deactivateStrategy = createAsyncThunk(
  'strategySymbols/deactivateStrategy',
  async ({ strategyId, symbol }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.deactivateStrategy(strategyId, symbol);
      return { strategyId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateStrategyConfig = createAsyncThunk(
  'strategySymbols/updateStrategyConfig',
  async ({ strategyId, symbol, config }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.updateConfig(strategyId, symbol, config);
      return { strategyId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteAssociation = createAsyncThunk(
  'strategySymbols/deleteAssociation',
  async ({ strategyId, symbol }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.deleteAssociation(strategyId, symbol);
      return { strategyId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const enableAllForStrategy = createAsyncThunk(
  'strategySymbols/enableAllForStrategy',
  async ({ strategyId, symbols, defaultConfig }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.enableAllForStrategy(strategyId, symbols, defaultConfig);
      return { strategyId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const disableAllForStrategy = createAsyncThunk(
  'strategySymbols/disableAllForStrategy',
  async ({ strategyId, symbols }, { rejectWithValue }) => {
    try {
      const response = await strategySymbolApi.disableAllForStrategy(strategyId, symbols);
      return { strategyId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const initialState = {
  // All user associations
  associations: [],
  associationsLoading: false,
  associationsError: null,
  
  // Current strategy's symbols (for configuration page)
  currentStrategySymbols: [],
  currentStrategyId: null,
  currentStrategyLoading: false,
  currentStrategyError: null,
  
  // UI state
  isActivating: false,
  isDeactivating: false,
  isUpdating: false,
  isDeleting: false,
  isBulkUpdating: false,
  
  // Success messages
  successMessage: null,
  
  // Available symbols (fallback list if API symbols are not available)
  availableSymbols: [
    'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
    'BTC/USD', 'ETH/USD', 'SOL/USD', 'ADA/USD',
    'SPY', 'QQQ', 'IWM', 'GLD', 'SLV'
  ]
};

const strategySymbolsSlice = createSlice({
  name: 'strategySymbols',
  initialState,
  reducers: {
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearErrors: (state) => {
      state.associationsError = null;
      state.currentStrategyError = null;
    },
    clearCurrentStrategy: (state) => {
      state.currentStrategySymbols = [];
      state.currentStrategyId = null;
      state.currentStrategyError = null;
    },
    addAvailableSymbol: (state, action) => {
      const symbol = action.payload.toUpperCase();
      if (!state.availableSymbols.includes(symbol)) {
        state.availableSymbols.push(symbol);
        state.availableSymbols.sort();
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user associations
      .addCase(fetchUserAssociations.pending, (state) => {
        state.associationsLoading = true;
        state.associationsError = null;
      })
      .addCase(fetchUserAssociations.fulfilled, (state, action) => {
        state.associationsLoading = false;
        state.associations = action.payload.associations || [];
      })
      .addCase(fetchUserAssociations.rejected, (state, action) => {
        state.associationsLoading = false;
        state.associationsError = action.payload;
      })
      
      // Fetch symbols for strategy
      .addCase(fetchSymbolsForStrategy.pending, (state) => {
        state.currentStrategyLoading = true;
        state.currentStrategyError = null;
      })
      .addCase(fetchSymbolsForStrategy.fulfilled, (state, action) => {
        state.currentStrategyLoading = false;
        state.currentStrategyId = action.payload.strategyId;
        state.currentStrategySymbols = action.payload.associations || [];
      })
      .addCase(fetchSymbolsForStrategy.rejected, (state, action) => {
        state.currentStrategyLoading = false;
        state.currentStrategyError = action.payload;
      })
      
      // Activate strategy
      .addCase(activateStrategy.pending, (state) => {
        state.isActivating = true;
      })
      .addCase(activateStrategy.fulfilled, (state, action) => {
        state.isActivating = false;
        state.successMessage = 'Strategy activated successfully';
        
        // Add to associations if not already there
        const newAssociation = action.payload.association;
        const existingIndex = state.associations.findIndex(
          assoc => assoc.strategySymbol === newAssociation.strategySymbol
        );
        
        if (existingIndex >= 0) {
          state.associations[existingIndex] = newAssociation;
        } else {
          state.associations.push(newAssociation);
        }
        
        // Update current strategy symbols if viewing this strategy
        if (state.currentStrategyId === newAssociation.strategyId) {
          const currentIndex = state.currentStrategySymbols.findIndex(
            assoc => assoc.symbol === newAssociation.symbol
          );
          
          if (currentIndex >= 0) {
            state.currentStrategySymbols[currentIndex] = newAssociation;
          } else {
            state.currentStrategySymbols.push(newAssociation);
          }
        }
      })
      .addCase(activateStrategy.rejected, (state, action) => {
        state.isActivating = false;
        state.associationsError = action.payload;
      })
      
      // Deactivate strategy
      .addCase(deactivateStrategy.pending, (state) => {
        state.isDeactivating = true;
      })
      .addCase(deactivateStrategy.fulfilled, (state, action) => {
        state.isDeactivating = false;
        state.successMessage = 'Strategy deactivated successfully';
        
        // Update the association in the list
        const updatedAssociation = action.payload.association;
        const associationIndex = state.associations.findIndex(
          assoc => assoc.strategyId === action.payload.strategyId && 
                   assoc.symbol === action.payload.symbol
        );
        
        if (associationIndex >= 0) {
          state.associations[associationIndex] = updatedAssociation;
        }
        
        // Update current strategy symbols
        if (state.currentStrategyId === action.payload.strategyId) {
          const currentIndex = state.currentStrategySymbols.findIndex(
            assoc => assoc.symbol === action.payload.symbol
          );
          
          if (currentIndex >= 0) {
            state.currentStrategySymbols[currentIndex] = updatedAssociation;
          }
        }
      })
      .addCase(deactivateStrategy.rejected, (state, action) => {
        state.isDeactivating = false;
        state.associationsError = action.payload;
      })
      
      // Update strategy config
      .addCase(updateStrategyConfig.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateStrategyConfig.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.successMessage = 'Configuration updated successfully';
        
        // Update the association in the list
        const updatedAssociation = action.payload.association;
        const associationIndex = state.associations.findIndex(
          assoc => assoc.strategyId === action.payload.strategyId && 
                   assoc.symbol === action.payload.symbol
        );
        
        if (associationIndex >= 0) {
          state.associations[associationIndex] = updatedAssociation;
        }
        
        // Update current strategy symbols
        if (state.currentStrategyId === action.payload.strategyId) {
          const currentIndex = state.currentStrategySymbols.findIndex(
            assoc => assoc.symbol === action.payload.symbol
          );
          
          if (currentIndex >= 0) {
            state.currentStrategySymbols[currentIndex] = updatedAssociation;
          }
        }
      })
      .addCase(updateStrategyConfig.rejected, (state, action) => {
        state.isUpdating = false;
        state.associationsError = action.payload;
      })
      
      // Delete association
      .addCase(deleteAssociation.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteAssociation.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'Association deleted successfully';
        
        // Remove from associations
        state.associations = state.associations.filter(
          assoc => !(assoc.strategyId === action.payload.strategyId && 
                     assoc.symbol === action.payload.symbol)
        );
        
        // Remove from current strategy symbols
        if (state.currentStrategyId === action.payload.strategyId) {
          state.currentStrategySymbols = state.currentStrategySymbols.filter(
            assoc => assoc.symbol !== action.payload.symbol
          );
        }
      })
      .addCase(deleteAssociation.rejected, (state, action) => {
        state.isDeleting = false;
        state.associationsError = action.payload;
      })

      // Enable all for strategy
      .addCase(enableAllForStrategy.pending, (state) => {
        state.isBulkUpdating = true;
      })
      .addCase(enableAllForStrategy.fulfilled, (state, action) => {
        state.isBulkUpdating = false;
        state.successMessage = 'All symbols enabled successfully';

        const { strategyId, updated = [] } = action.payload;

        updated.forEach((association) => {
          // Update global associations
          const associationIndex = state.associations.findIndex(
            (assoc) => assoc.strategySymbol === association.strategySymbol
          );
          if (associationIndex >= 0) {
            state.associations[associationIndex] = association;
          } else {
            state.associations.push(association);
          }

          // Update current strategy symbols
          if (state.currentStrategyId === strategyId) {
            const currentIndex = state.currentStrategySymbols.findIndex(
              (assoc) => assoc.symbol === association.symbol
            );
            if (currentIndex >= 0) {
              state.currentStrategySymbols[currentIndex] = association;
            } else {
              state.currentStrategySymbols.push(association);
            }
          }
        });
      })
      .addCase(enableAllForStrategy.rejected, (state, action) => {
        state.isBulkUpdating = false;
        state.associationsError = action.payload;
      })

      // Disable all for strategy
      .addCase(disableAllForStrategy.pending, (state) => {
        state.isBulkUpdating = true;
      })
      .addCase(disableAllForStrategy.fulfilled, (state, action) => {
        state.isBulkUpdating = false;
        state.successMessage = 'All symbols disabled successfully';

        const { strategyId, deleted = [] } = action.payload;

        deleted.forEach((association) => {
          // Remove from global associations
          state.associations = state.associations.filter(
            (assoc) => assoc.strategySymbol !== association.strategySymbol
          );

          // Remove from current strategy symbols
          if (state.currentStrategyId === strategyId) {
            state.currentStrategySymbols = state.currentStrategySymbols.filter(
              (assoc) => assoc.symbol !== association.symbol
            );
          }
        });
      })
      .addCase(disableAllForStrategy.rejected, (state, action) => {
        state.isBulkUpdating = false;
        state.associationsError = action.payload;
      });
  }
});

export const { 
  clearSuccessMessage, 
  clearErrors, 
  clearCurrentStrategy,
  addAvailableSymbol 
} = strategySymbolsSlice.actions;

export default strategySymbolsSlice.reducer;